/**
 * The static description of the scheduler theme system: which directions ship,
 * and the catalog of variables the tweak-your-own editor can reach.
 *
 * The catalog is a presentation-order listing of what `_scheduler-theme.scss`
 * already defines — it does not hold any values. Values always come from the
 * cascade (`getComputedStyle` on `<html>`), so a palette edited in the
 * stylesheet shows up in the editor without anyone touching this file. What
 * lives here is only what CSS cannot tell us: a human label, which group a
 * variable belongs under, and what kind of control can edit it.
 *
 * This sits below `SchedulerThemeService` rather than inside it so the codec in
 * `scheduler-custom-theme.ts` can validate against it without the two files
 * importing each other.
 *
 * A variable missing from the catalog still themes fine; the editor just
 * cannot reach it. So a new `--sched-*` variable in the stylesheet wants an
 * entry here too.
 */

/**
 * The scheduler color directions that ship side by side. Each corresponds to a
 * handoff bundle under `specs/`, and to a palette block in
 * `src/styles/schedule/_scheduler-theme.scss`.
 */
export type SchedulerThemeId =
  | 'rail'
  | 'cool-triad'
  | 'paper'
  | 'steel-lines'
  | 'steel-filled'
  | 'classic';

export interface SchedulerThemeOption {
  id: SchedulerThemeId;
  label: string;
  /** One line for the cog menu, so the choice is legible without the specs. */
  description: string;
}

export const SCHEDULER_THEMES: SchedulerThemeOption[] = [
  {
    id: 'rail',
    label: 'Rail',
    description: 'Pale cells with a colored leading edge',
  },
  {
    id: 'cool-triad',
    label: 'Cool triad',
    description: 'Full fills, redrawn cooler and lighter',
  },
  {
    id: 'paper',
    label: 'Paper',
    description: 'Tints separated by hairline ink rules',
  },
  {
    id: 'steel-lines',
    label: 'Steel lines',
    description: 'Content on white with neutral hairlines',
  },
  {
    id: 'steel-filled',
    label: 'Steel filled',
    description: 'Steel fills separated by white grid lines',
  },
  {
    id: 'classic',
    label: 'Classic',
    description: 'The original colors, before the redesign',
  },
];

export function isSchedulerThemeId(value: unknown): value is SchedulerThemeId {
  return SCHEDULER_THEMES.some((t) => t.id === value);
}

export type SchedulerTokenKind =
  /** Anything that resolves to a paint. Swatch + free text, so `transparent` stays typeable. */
  | 'color'
  /** A px length. */
  | 'px'
  /** A CSS font weight, 100..900. */
  | 'weight'
  /** A 0..1 ratio. */
  | 'ratio'
  /** A whole declaration (`1px solid <color>`) or an alias. Free text only. */
  | 'text';

export interface SchedulerToken {
  /** The custom property name, including the leading `--`. */
  name: string;
  /** Shown in the editor row. */
  label: string;
  kind: SchedulerTokenKind;
}

export interface SchedulerTokenGroup {
  title: string;
  /** One line under the group heading; omitted where the title says it all. */
  hint?: string;
  tokens: SchedulerToken[];
  /**
   * Groups that stay shut until asked for. The four roles and the grid chrome
   * are what anyone actually wants to tweak; the rest is long.
   */
  collapsed?: boolean;
}

/** The eight variables every cell role defines, in the stylesheet's order. */
function roleTokens(role: string): SchedulerToken[] {
  return [
    { name: `--sched-${role}-fill`, label: 'Fill', kind: 'color' },
    { name: `--sched-${role}-ink`, label: 'Text', kind: 'color' },
    { name: `--sched-${role}-fill-hover`, label: 'Fill · hover', kind: 'color' },
    { name: `--sched-${role}-fill-selected`, label: 'Fill · selected', kind: 'color' },
    { name: `--sched-${role}-rail`, label: 'Leading edge', kind: 'color' },
    { name: `--sched-${role}-summary-fill`, label: 'Summary row fill', kind: 'color' },
    { name: `--sched-${role}-rule`, label: 'Right-hand rule', kind: 'color' },
    { name: `--sched-${role}-rule-selected`, label: 'Right-hand rule · selected', kind: 'color' },
  ];
}

export const SCHEDULER_TOKEN_GROUPS: SchedulerTokenGroup[] = [
  {
    title: 'Tooling',
    hint: 'Columns carrying tooling data.',
    tokens: roleTokens('tooling'),
  },
  {
    title: 'Material',
    hint: 'Columns carrying coil and material data.',
    tokens: roleTokens('material'),
  },
  {
    title: 'Job',
    hint: 'Columns carrying job data.',
    tokens: roleTokens('job'),
  },
  {
    title: 'Job card',
    hint: 'The dense card in the narrow layout.',
    tokens: roleTokens('jobcard'),
  },
  {
    title: 'Grid chrome',
    tokens: [
      { name: '--sched-cell-gap', label: 'Cell gap', kind: 'color' },
      { name: '--sched-header-ground', label: 'Header ground', kind: 'color' },
      { name: '--sched-header-text', label: 'Header text', kind: 'color' },
      { name: '--sched-header-rule', label: 'Header rule', kind: 'color' },
      { name: '--sched-machine-label', label: 'Machine label', kind: 'color' },
      { name: '--sched-card-secondary-text', label: 'Card secondary text', kind: 'color' },
      { name: '--sched-focus-ring', label: 'Focus ring', kind: 'color' },
      { name: '--sched-row-rule', label: 'Row rule', kind: 'color' },
      { name: '--sched-last-cell-rule', label: 'Last cell rule', kind: 'color' },
    ],
  },
  {
    title: 'Geometry',
    hint: 'Row height feeds the virtual scrollers, so it reflows the grid.',
    tokens: [
      { name: '--sched-grid-row-height', label: 'Data row height', kind: 'px' },
      { name: '--sched-header-height', label: 'Header height', kind: 'px' },
      { name: '--sched-rail-width', label: 'Rail width', kind: 'px' },
      { name: '--sched-rail-width-selected', label: 'Rail width · selected', kind: 'px' },
      { name: '--sched-rail-width-header', label: 'Rail width · header', kind: 'px' },
      { name: '--sched-cell-rule-width', label: 'Cell rule width', kind: 'px' },
      { name: '--sched-cell-rule-width-selected', label: 'Cell rule width · selected', kind: 'px' },
      { name: '--sched-cell-weight', label: 'Cell font weight', kind: 'weight' },
      { name: '--sched-rail-cell-weight', label: 'Rail cell font weight', kind: 'weight' },
      { name: '--sched-band-label-weight', label: 'Band label font weight', kind: 'weight' },
    ],
  },
  {
    title: 'Timeline lane',
    collapsed: true,
    tokens: [
      { name: '--sched-lane-ground', label: 'Lane ground', kind: 'color' },
      { name: '--sched-lane-border', label: 'Lane border', kind: 'color' },
      { name: '--sched-lane-opacity', label: 'Lane opacity', kind: 'ratio' },
      { name: '--sched-now-line', label: 'Now line', kind: 'color' },
      { name: '--sched-hatch-ground', label: 'Hatch ground', kind: 'color' },
      { name: '--sched-hatch-stroke', label: 'Hatch stroke', kind: 'color' },
      { name: '--sched-triangle-error', label: 'Error triangle', kind: 'color' },
      { name: '--sched-triangle-warning', label: 'Warning triangle', kind: 'color' },
      { name: '--sched-block-unscheduled-edge', label: 'Unscheduled edge', kind: 'color' },
    ],
  },
  {
    title: 'Timeline blocks',
    collapsed: true,
    tokens: [
      { name: '--sched-block-production', label: 'Production', kind: 'color' },
      { name: '--sched-block-production-grad-left', label: 'Production · ramp left', kind: 'color' },
      { name: '--sched-block-production-grad-right', label: 'Production · ramp right', kind: 'color' },
      { name: '--sched-block-production-rail', label: 'Production · rail', kind: 'color' },
      { name: '--sched-block-production-label', label: 'Production · label', kind: 'color' },
      { name: '--sched-block-production-stroke', label: 'Production · stroke', kind: 'color' },
      { name: '--sched-block-changeover', label: 'Changeover', kind: 'color' },
      { name: '--sched-block-changeover-grad-left', label: 'Changeover · ramp left', kind: 'color' },
      {
        name: '--sched-block-changeover-grad-right',
        label: 'Changeover · ramp right',
        kind: 'color',
      },
      { name: '--sched-block-changeover-rail', label: 'Changeover · rail', kind: 'color' },
      { name: '--sched-block-changeover-label', label: 'Changeover · label', kind: 'color' },
      { name: '--sched-block-changeover-stroke', label: 'Changeover · stroke', kind: 'color' },
      { name: '--sched-block-machineconfig', label: 'Machine config', kind: 'color' },
      { name: '--sched-block-machineconfig-stroke', label: 'Machine config · stroke', kind: 'color' },
      { name: '--sched-block-break', label: 'Break', kind: 'color' },
      { name: '--sched-block-break-rail', label: 'Break · rail', kind: 'color' },
      { name: '--sched-block-break-stroke', label: 'Break · stroke', kind: 'color' },
      { name: '--sched-block-meeting', label: 'Meeting', kind: 'color' },
      { name: '--sched-block-meeting-rail', label: 'Meeting · rail', kind: 'color' },
      { name: '--sched-block-meeting-stroke', label: 'Meeting · stroke', kind: 'color' },
      { name: '--sched-block-maintenance', label: 'Maintenance', kind: 'color' },
      { name: '--sched-block-maintenance-rail', label: 'Maintenance · rail', kind: 'color' },
      { name: '--sched-block-maintenance-stroke', label: 'Maintenance · stroke', kind: 'color' },
      { name: '--sched-block-coilchange', label: 'Coil change', kind: 'color' },
      { name: '--sched-block-bundlechange', label: 'Bundle change', kind: 'color' },
      { name: '--sched-block-breakdown', label: 'Breakdown', kind: 'color' },
    ],
  },
  {
    title: 'Timeline tooltip',
    collapsed: true,
    tokens: [
      { name: '--sched-tip-production', label: 'Production', kind: 'color' },
      { name: '--sched-tip-changeover', label: 'Changeover', kind: 'color' },
      { name: '--sched-tip-coilchange', label: 'Coil change', kind: 'color' },
      { name: '--sched-tip-break', label: 'Break', kind: 'color' },
      { name: '--sched-tip-meeting', label: 'Meeting', kind: 'color' },
      { name: '--sched-tip-maintenance', label: 'Maintenance', kind: 'color' },
      { name: '--sched-tip-bundlechange', label: 'Bundle change', kind: 'color' },
      { name: '--sched-tip-duration-ground', label: 'Duration ground', kind: 'color' },
      { name: '--sched-tip-duration-text', label: 'Duration text', kind: 'color' },
      { name: '--sched-tip-total-row-ground', label: 'Total row ground', kind: 'color' },
    ],
  },
  {
    title: 'Cell borders',
    hint: 'Whole declarations, so a direction can switch a border off with a plain 0.',
    collapsed: true,
    tokens: [
      { name: '--sched-cell-border-y', label: 'Top / bottom', kind: 'text' },
      { name: '--sched-cell-border-x', label: 'Left / right', kind: 'text' },
      { name: '--sched-cell-border-b', label: 'Bottom', kind: 'text' },
    ],
  },
];

/** Every token in the catalog, flattened. */
export const SCHEDULER_TOKENS: SchedulerToken[] = SCHEDULER_TOKEN_GROUPS.flatMap((g) => g.tokens);

const TOKEN_NAMES = new Set(SCHEDULER_TOKENS.map((t) => t.name));

/**
 * Is this a variable the editor is allowed to write?
 *
 * Imported themes arrive as text pasted from someone else, so the names in
 * them are untrusted: without this check a hand-edited payload could set any
 * inline custom property it liked on `<html>`.
 */
export function isSchedulerToken(name: string): boolean {
  return TOKEN_NAMES.has(name);
}
